<template>
  <div class="space-y-6">
    <div class="bg-gradient-to-r from-amber-500 to-red-600 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Reportes</h1>
          <p class="text-amber-100 mt-1">Gestión de reportes de usuarios y mensajes</p>
        </div>
        <div class="hidden sm:flex space-x-4">
          <div class="bg-white/20 rounded-lg p-3 text-center">
            <p class="text-2xl font-bold">{{ stats.userReports?.pending || 0 }}</p>
            <p class="text-xs text-amber-100">Usuarios Pendientes</p>
          </div>
          <div class="bg-white/20 rounded-lg p-3 text-center">
            <p class="text-2xl font-bold">{{ stats.messageReports?.pending || 0 }}</p>
            <p class="text-xs text-amber-100">Mensajes Pendientes</p>
          </div>
        </div>
      </div>
    </div>

    <div class="card p-6">
      <div class="flex flex-col lg:flex-row gap-4 items-end">
        <div class="flex space-x-2">
          <button
            @click="activeTab = 'users'"
            class="px-4 py-2 rounded-lg font-medium transition-all duration-200"
            :class="activeTab === 'users' ? 'bg-amber-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
          >
            Reportes de Usuarios
          </button>
          <button
            @click="activeTab = 'messages'"
            class="px-4 py-2 rounded-lg font-medium transition-all duration-200"
            :class="activeTab === 'messages' ? 'bg-amber-600 text-white shadow-lg' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
          >
            Reportes de Mensajes
          </button>
        </div>
        <div class="w-full lg:flex-1">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Buscar..."
            class="block w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
            @input="debouncedSearch"
          />
        </div>
        <div>
          <select v-model="statusFilter" @change="loadReports" class="block px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500">
            <option value="">Todos los estados</option>
            <option value="pending">Pendiente</option>
            <option value="reviewed">Revisado</option>
            <option value="resolved">Resuelto</option>
            <option value="dismissed">Descartado</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="activeTab === 'users'" class="card">
      <DataTable
        :value="userReports"
        :loading="loading"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        :totalRecords="userPagination.total"
        :lazy="true"
        @page="onUserPage"
        tableStyle="min-width: 50rem"
        class="p-datatable-sm"
      >
        <template #header>
          <h2 class="text-lg font-semibold text-gray-900">Reportes de Usuarios</h2>
        </template>

        <template #empty>
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">Sin reportes</h3>
            <p class="mt-1 text-sm text-gray-500">No se encontraron reportes de usuarios.</p>
          </div>
        </template>

        <Column header="Reportante">
          <template #body="{ data }">
            <div class="flex items-center">
              <div class="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-xs mr-2">
                {{ data.reporter?.name?.charAt(0).toUpperCase() }}
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900">{{ data.reporter?.name }}</p>
                <p class="text-xs text-gray-500">{{ data.reporter?.email }}</p>
              </div>
            </div>
          </template>
        </Column>

        <Column header="Reportado">
          <template #body="{ data }">
            <div class="flex items-center">
              <div class="h-8 w-8 rounded-full bg-gradient-to-r from-red-500 to-orange-600 flex items-center justify-center text-white font-semibold text-xs mr-2">
                {{ data.reported?.name?.charAt(0).toUpperCase() }}
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900">{{ data.reported?.name }}</p>
                <p class="text-xs text-gray-500">{{ data.reported?.email }}</p>
              </div>
            </div>
          </template>
        </Column>

        <Column field="reason" header="Razón">
          <template #body="{ data }">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="reasonClass(data.reason)">
              {{ reasonLabel(data.reason) }}
            </span>
          </template>
        </Column>

        <Column field="details" header="Detalles">
          <template #body="{ data }">
            <span class="text-sm text-gray-700 truncate block max-w-xs">{{ data.details || '-' }}</span>
          </template>
        </Column>

        <Column field="status" header="Estado">
          <template #body="{ data }">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="statusClass(data.status)">
              {{ statusLabel(data.status) }}
            </span>
          </template>
        </Column>

        <Column field="createdAt" header="Fecha" sortable>
          <template #body="{ data }">
            <span class="text-sm text-gray-900">{{ new Date(data.createdAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' }) }}</span>
          </template>
        </Column>

        <Column header="Acciones" style="width: 80px">
          <template #body="{ data }">
            <button
              @click="goToDetail('user', data.id)"
              class="inline-flex items-center p-1.5 text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 transition-all duration-200"
              title="Ver detalle"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            </button>
          </template>
        </Column>
      </DataTable>
    </div>

    <div v-if="activeTab === 'messages'" class="card">
      <DataTable
        :value="messageReports"
        :loading="loading"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        :totalRecords="messagePagination.total"
        :lazy="true"
        @page="onMessagePage"
        tableStyle="min-width: 50rem"
        class="p-datatable-sm"
      >
        <template #header>
          <h2 class="text-lg font-semibold text-gray-900">Reportes de Mensajes</h2>
        </template>

        <template #empty>
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">Sin reportes</h3>
            <p class="mt-1 text-sm text-gray-500">No se encontraron reportes de mensajes.</p>
          </div>
        </template>

        <Column header="Reportante">
          <template #body="{ data }">
            <div class="flex items-center">
              <div class="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-xs mr-2">
                {{ data.reporter?.name?.charAt(0).toUpperCase() }}
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900">{{ data.reporter?.name }}</p>
                <p class="text-xs text-gray-500">{{ data.reporter?.email }}</p>
              </div>
            </div>
          </template>
        </Column>

        <Column header="Autor del Mensaje">
          <template #body="{ data }">
            <div class="flex items-center">
              <div class="h-8 w-8 rounded-full bg-gradient-to-r from-gray-500 to-gray-600 flex items-center justify-center text-white font-semibold text-xs mr-2">
                {{ data.message?.sender?.name?.charAt(0).toUpperCase() }}
              </div>
              <div>
                <p class="text-sm font-medium text-gray-900">{{ data.message?.sender?.name }}</p>
                <p class="text-xs text-gray-500">{{ data.message?.sender?.email }}</p>
              </div>
            </div>
          </template>
        </Column>

        <Column header="Mensaje Reportado">
          <template #body="{ data }">
            <div class="max-w-xs">
              <div v-if="data.message?.messageType === 'image'" class="flex items-center text-sm text-gray-500">
                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Imagen
              </div>
              <p v-else class="text-sm text-gray-700 truncate">{{ data.message?.content || '-' }}</p>
              <p class="text-xs text-gray-400 mt-1">{{ data.message?.createdAt ? new Date(data.message.createdAt).toLocaleString('es-ES') : '' }}</p>
            </div>
          </template>
        </Column>

        <Column field="reason" header="Razón">
          <template #body="{ data }">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="reasonClass(data.reason)">
              {{ reasonLabel(data.reason) }}
            </span>
          </template>
        </Column>

        <Column field="status" header="Estado">
          <template #body="{ data }">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="statusClass(data.status)">
              {{ statusLabel(data.status) }}
            </span>
          </template>
        </Column>

        <Column field="createdAt" header="Fecha" sortable>
          <template #body="{ data }">
            <span class="text-sm text-gray-900">{{ new Date(data.createdAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' }) }}</span>
          </template>
        </Column>

        <Column header="Acciones" style="width: 80px">
          <template #body="{ data }">
            <button
              @click="goToDetail('message', data.id)"
              class="inline-flex items-center p-1.5 text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 transition-all duration-200"
              title="Ver detalle"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            </button>
          </template>
        </Column>
      </DataTable>
    </div>
    <div v-if="showBlockReasonModal" class="fixed inset-0 z-[70] overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showBlockReasonModal = false"></div>
        <div class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          <div class="bg-red-600 px-6 py-4">
            <h3 class="text-lg font-bold text-white">Bloquear acceso al app</h3>
            <p class="text-red-100 text-sm mt-0.5">El usuario no podrá iniciar sesión hasta que sea desbloqueado</p>
          </div>
          <div class="bg-white px-6 py-5">
            <label class="block text-sm font-medium text-gray-700 mb-2">Motivo del bloqueo (opcional)</label>
            <textarea
              v-model="blockReasonText"
              rows="3"
              placeholder="Ej: Comportamiento inapropiado, acoso a otros usuarios..."
              class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
            ></textarea>
          </div>
          <div class="bg-gray-50 px-6 py-3 flex flex-row-reverse gap-2">
            <button @click="executeBlock" :disabled="actionLoading" class="btn btn-danger">{{ actionLoading ? 'Bloqueando...' : 'Confirmar bloqueo' }}</button>
            <button @click="showBlockReasonModal = false" class="btn btn-secondary">Cancelar</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDeleteModal" class="fixed inset-0 z-[60] overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showDeleteModal = false"></div>
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Eliminar Reporte</h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">¿Estás seguro de que quieres eliminar este reporte? Esta acción no se puede deshacer.</p>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button @click="executeDelete" :disabled="deleting" class="btn btn-danger w-full sm:w-auto sm:ml-3">
              {{ deleting ? 'Eliminando...' : 'Eliminar' }}
            </button>
            <button @click="showDeleteModal = false" class="btn btn-secondary w-full sm:w-auto mt-3 sm:mt-0">
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import reportService from '@/services/reportService'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

export default {
  name: 'Reports',
  components: { DataTable, Column },
  setup() {
    const router = useRouter()
    const toast = useToast()
    const loading = ref(false)
    const deleting = ref(false)
    const activeTab = ref('users')
    const searchQuery = ref('')
    const statusFilter = ref('')
    const showDeleteModal = ref(false)
    const deleteTarget = ref(null)
    const deleteType = ref('')
    const showBlockReasonModal = ref(false)
    const blockReasonText = ref('')
    const pendingBlockAction = ref(null)

    const userReports = ref([])
    const messageReports = ref([])
    const stats = reactive({ userReports: { total: 0, pending: 0 }, messageReports: { total: 0, pending: 0 } })
    const userPagination = reactive({ page: 1, limit: 10, total: 0, totalPages: 0 })
    const messagePagination = reactive({ page: 1, limit: 10, total: 0, totalPages: 0 })
    let searchTimeout = null

    const reasonLabels = {
      spam: 'Spam',
      harassment: 'Acoso',
      inappropriate_content: 'Contenido inapropiado',
      fake_profile: 'Perfil falso',
      scam: 'Estafa',
      violence: 'Violencia',
      hate_speech: 'Discurso de odio',
      other: 'Otro'
    }

    const reasonColors = {
      spam: 'bg-gray-100 text-gray-800',
      harassment: 'bg-red-100 text-red-800',
      inappropriate_content: 'bg-orange-100 text-orange-800',
      fake_profile: 'bg-yellow-100 text-yellow-800',
      scam: 'bg-purple-100 text-purple-800',
      violence: 'bg-red-100 text-red-800',
      hate_speech: 'bg-red-100 text-red-800',
      other: 'bg-blue-100 text-blue-800'
    }

    const statusLabels = {
      pending: 'Pendiente',
      reviewed: 'Revisado',
      resolved: 'Resuelto',
      dismissed: 'Descartado'
    }

    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewed: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      dismissed: 'bg-gray-100 text-gray-800'
    }

    const reasonLabel = (reason) => reasonLabels[reason] || reason
    const reasonClass = (reason) => reasonColors[reason] || 'bg-gray-100 text-gray-800'
    const statusLabel = (status) => statusLabels[status] || status
    const statusClass = (status) => statusColors[status] || 'bg-gray-100 text-gray-800'
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'

    const loadUserReports = async () => {
      loading.value = true
      try {
        const response = await reportService.getUserReports({
          page: userPagination.page,
          limit: userPagination.limit,
          search: searchQuery.value,
          status: statusFilter.value
        })
        userReports.value = response.data.data
        Object.assign(userPagination, response.data.pagination)
      } catch {
        toast.error('Error al cargar reportes de usuarios')
      } finally {
        loading.value = false
      }
    }

    const loadMessageReports = async () => {
      loading.value = true
      try {
        const response = await reportService.getMessageReports({
          page: messagePagination.page,
          limit: messagePagination.limit,
          search: searchQuery.value,
          status: statusFilter.value
        })
        messageReports.value = response.data.data
        Object.assign(messagePagination, response.data.pagination)
      } catch {
        toast.error('Error al cargar reportes de mensajes')
      } finally {
        loading.value = false
      }
    }

    const loadReports = () => {
      if (activeTab.value === 'users') loadUserReports()
      else loadMessageReports()
    }

    const loadStats = async () => {
      try {
        const response = await reportService.getReportStats()
        Object.assign(stats, response.data)
      } catch {}
    }

    const debouncedSearch = () => {
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        userPagination.page = 1
        messagePagination.page = 1
        loadReports()
      }, 300)
    }

    const onUserPage = (event) => {
      userPagination.page = event.page + 1
      userPagination.limit = event.rows
      loadUserReports()
    }

    const onMessagePage = (event) => {
      messagePagination.page = event.page + 1
      messagePagination.limit = event.rows
      loadMessageReports()
    }

    const openUserReportDetail = (report) => {
      router.push({ name: 'ReportDetail', params: { type: 'user', id: report.id } })
    }

    const openMessageReportDetail = (report) => {
      router.push({ name: 'ReportDetail', params: { type: 'message', id: report.id } })
    }

    const goToDetail = (type, id) => {
      router.push({ name: 'ReportDetail', params: { type, id } })
    }

    const openBlockReasonModal = (reportId, userId, context) => {
      pendingBlockAction.value = { reportId, userId, context }
      blockReasonText.value = ''
      showBlockReasonModal.value = true
    }

    const executeBlock = async () => {
      const { reportId, userId } = pendingBlockAction.value
      try {
        await reportService.blockUser(reportId, userId, 'block', blockReasonText.value || undefined)
        toast.success('Usuario bloqueado del app')
        showBlockReasonModal.value = false
      } catch {
        toast.error('Error al bloquear al usuario')
      }
    }

    const confirmDeleteUserReport = (report) => {
      deleteTarget.value = report
      deleteType.value = 'user'
      showDeleteModal.value = true
    }

    const confirmDeleteMessageReport = (report) => {
      deleteTarget.value = report
      deleteType.value = 'message'
      showDeleteModal.value = true
    }

    const executeDelete = async () => {
      deleting.value = true
      try {
        if (deleteType.value === 'user') {
          await reportService.deleteUserReport(deleteTarget.value.id)
        } else {
          await reportService.deleteMessageReport(deleteTarget.value.id)
        }
        toast.success('Reporte eliminado correctamente')
        showDeleteModal.value = false
        deleteTarget.value = null
        loadReports()
        loadStats()
      } catch {
        toast.error('Error al eliminar reporte')
      } finally {
        deleting.value = false
      }
    }

    watch(activeTab, () => {
      loadReports()
    })

    onMounted(() => {
      loadUserReports()
      loadMessageReports()
      loadStats()
    })

    return {
      loading, deleting, activeTab, searchQuery, statusFilter,
      showDeleteModal, showBlockReasonModal, blockReasonText,
      deleteTarget, userReports, messageReports, stats,
      userPagination, messagePagination,
      reasonLabel, reasonClass, statusLabel, statusClass, formatDate,
      debouncedSearch, onUserPage, onMessagePage, loadReports,
      goToDetail, openUserReportDetail, openMessageReportDetail,
      openBlockReasonModal, executeBlock,
      confirmDeleteUserReport, confirmDeleteMessageReport, executeDelete
    }
  }
}
</script>
